import React from "react";

interface AuthShellProps {
  eyebrow?: string;
  title: string;
  description: string;
  children: React.ReactNode;
}

const COLORS = {
  teal: "#27a7a1",
  blue: "#6d90b2",
  coral: "#f56f52",
  green: "#9db85f",
  black: "#5f5f63",
};

const AuthShell: React.FC<AuthShellProps> = ({
  eyebrow,
  title,
  description,
  children,
}) => {
  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,rgba(0,128,128,0.06),transparent_30%),linear-gradient(180deg,#ffffff_0%,#f8fbfb_100%)] px-4 py-6 lg:px-8 lg:py-10">
      <div className="mx-auto flex min-h-[calc(100vh-3rem)] w-full max-w-6xl overflow-hidden rounded-[44px] bg-white shadow-[0_28px_90px_rgba(15,23,42,0.12)] ring-1 ring-slate-200/80 lg:min-h-[720px]">
        <aside className="relative hidden w-[42%] overflow-hidden border-r border-slate-200/80 bg-[linear-gradient(180deg,#ffffff_0%,#fdfefe_42%,#eef8f8_100%)] px-10 py-10 text-slate-900 lg:flex xl:px-12">
          <div className="absolute inset-x-0 bottom-0 h-52 bg-[radial-gradient(circle_at_bottom_left,rgba(0,128,128,0.12),transparent_58%)]" />
          <div className="absolute left-0 top-0 h-full w-full bg-[radial-gradient(circle_at_top_left,rgba(0,128,128,0.08),transparent_26%)]" />

          <div className="relative flex w-full flex-col">
            <div className="inline-flex w-fit items-center rounded-full border border-primary/20 bg-white/90 px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.28em] text-primary shadow-[0_10px_30px_rgba(15,23,42,0.08)]">
              Deaf-led learning platform
            </div>

            <div className="mt-10 flex justify-center">
              <div className="flex w-full max-w-[24rem] flex-col items-center rounded-[32px] bg-white/70 px-6 py-7 shadow-[0_18px_48px_rgba(15,23,42,0.08)] ring-1 ring-slate-100/90 backdrop-blur">
                <div
                  aria-label="eDeaf logo"
                  className="inline-flex items-end justify-center whitespace-nowrap text-[4.25rem] font-bold leading-none tracking-[-0.085em] xl:text-[5rem]"
                >
                  <span
                    className="relative top-[0.035em] inline-block text-[0.8em]"
                    style={{ color: COLORS.black }}
                  >
                    e
                  </span>
                  <span className="inline-block" style={{ color: COLORS.teal }}>
                    D
                  </span>
                  <span className="inline-block" style={{ color: COLORS.blue }}>
                    E
                  </span>
                  <span
                    className="inline-block"
                    style={{ color: COLORS.coral }}
                  >
                    A
                  </span>
                  <span
                    className="inline-block"
                    style={{ color: COLORS.green }}
                  >
                    F
                  </span>
                </div>
                <p className="mt-1 text-[0.95rem] font-medium uppercase tracking-[0.16em] text-slate-500 xl:text-[1.05rem]">
                  Employ + Empower
                </p>
              </div>
            </div>

            <div className="mt-8 max-w-sm text-left">
              <h2 className="text-4xl font-semibold leading-tight tracking-[-0.03em] text-slate-950">
                Empowering Deaf Youth With Skills, Support, and Opportunity
              </h2>
              <p className="mt-4 max-w-md text-base leading-7 text-slate-500">
                Accessible, Deaf-led training and support services that help
                Deaf learners build confidence, gain qualifications, and prepare
                for meaningful employment.
              </p>
            </div>
          </div>
        </aside>

        <main className="flex flex-1 items-center justify-center bg-[linear-gradient(180deg,#fbfdfd_0%,#f7fbfb_100%)] px-5 py-8 sm:px-8 lg:px-10">
          <div className="w-full max-w-md rounded-[32px] bg-white/88 p-6 shadow-[0_24px_70px_rgba(15,23,42,0.06)] backdrop-blur sm:p-7">
            {eyebrow ? (
              <div className="mb-3 inline-flex rounded-full bg-primary/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-primary">
                {eyebrow}
              </div>
            ) : null}
            <h1 className="text-3xl font-semibold tracking-[-0.03em] text-slate-900 sm:text-4xl">
              {title}
            </h1>
            <p className="mt-3 text-sm leading-7 text-slate-500 sm:text-base">
              {description}
            </p>

            <div className="mt-6">{children}</div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AuthShell;
